import React from "react";
import { Link } from "react-router-dom";
import { TRACKS } from "@/constants/tracks";
import { TRACKS as T_IDS } from "@/constants/testIds";

function TrackCard({ track, layout }) {
  // layout: "flagship" (col-span-8) | "secondary" (col-span-4)
  const isFlagship = layout === "flagship";

  return (
    <Link
      to={`/track/${track.slug}`}
      data-testid={T_IDS.card(track.slug)}
      className={`iw-track-card group relative flex flex-col justify-between overflow-hidden ${
        isFlagship
          ? "lg:col-span-8 min-h-[520px]"
          : "lg:col-span-4 min-h-[320px]"
      }`}
    >
      {/* Top: image */}
      <div
        className={`relative w-full overflow-hidden ${
          isFlagship ? "h-[300px] lg:h-[340px]" : "h-[180px] lg:h-[200px]"
        }`}
      >
        <img
          src={track.image}
          alt={track.name}
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#11296B]/55 to-transparent" />
        <div className="absolute left-4 top-4 flex items-center gap-2">
          <span className="border border-white/30 bg-white/10 px-2.5 py-1 font-mono text-[12px] uppercase tracking-[0.22em] text-white backdrop-blur">
            {track.code}
          </span>
          {track.flagship && (
            <span className="bg-[color:var(--iw-accent-red)] px-2.5 py-1 font-mono text-[12px] uppercase tracking-[0.22em] text-white">
              Flagship
            </span>
          )}
        </div>
      </div>

      {/* Bottom: content */}
      <div className="flex flex-1 flex-col justify-between p-6 lg:p-8">
        <div>
          <h3
            className={`font-display font-semibold tracking-tight text-[color:var(--iw-primary)] ${
              isFlagship ? "text-3xl lg:text-4xl" : "text-2xl"
            }`}
          >
            {track.name}
          </h3>
          <p
            className={`mt-2 font-display text-[color:var(--iw-accent-red)] ${
              isFlagship ? "text-base" : "text-sm"
            }`}
          >
            {track.tagline}
          </p>
          {track.subline && (
            <p className="mt-1 font-mono text-[12px] uppercase tracking-[0.22em] text-[color:var(--iw-muted-foreground)]">
              {track.subline}
            </p>
          )}
          {isFlagship && (
            <p className="mt-5 max-w-xl text-base leading-relaxed text-[color:var(--iw-body)]">
              {track.summary}
            </p>
          )}
        </div>

        {/* Accent underline */}
        <div className="iw-track-accent absolute bottom-0 left-0 h-[3px] w-12 bg-[color:var(--iw-accent-red)]" />
      </div>
    </Link>
  );
}

export default function TracksSection() {
  const flagship = TRACKS.find((t) => t.flagship);
  const others = TRACKS.filter((t) => !t.flagship);

  return (
    <section
      id="tracks"
      data-testid={T_IDS.root}
      className="border-b border-[color:var(--iw-border)] bg-[color:var(--iw-bg)] py-20 sm:py-28"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <div className="max-w-2xl">
            <div className="iw-overline mb-3">§ 01 — Programs</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight text-[color:var(--iw-primary)] sm:text-4xl lg:text-5xl">
              Eight programs.{" "}
              <span className="text-[color:var(--iw-blue)]">Built to work together.</span>
            </h2>
          </div>
          <p className="max-w-md text-lg text-[color:var(--iw-body)]">
            Start with one. Add more as you go. They're built to compound.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:gap-6">
          {flagship && <TrackCard track={flagship} layout="flagship" />}
          {others.map((t) => (
            <TrackCard key={t.slug} track={t} layout="secondary" />
          ))}
        </div>
      </div>
    </section>
  );
}
